import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import { getUserModelSpending } from '@/services/user_usage';
import { useAuthStore } from '@/stores/auth';
import { useTenantStore } from '@/stores/tenant';
import { useUserStore } from '@/stores/user';
import type { UserModelSpending } from '@/types';

const NOTICE_THRESHOLD = 75;
const CRITICAL_THRESHOLD = 95;
const LOCKED_THRESHOLD = 100;

export type ModelSpendingState =
    | 'calm'
    | 'notice'
    | 'critical'
    | 'locked'
    | 'unavailable';

export const useModelSpendingStore = defineStore('modelSpending', () => {
    const authStore = useAuthStore();
    const tenantStore = useTenantStore();
    const userStore = useUserStore();

    const spending = ref<UserModelSpending | null>(null);
    const loading = ref(false);
    const activeRequestId = ref(0);
    const lastTenantId = ref<string>();
    const lastUserId = ref<string>();
    // True once we have at least one resolved fetch (success or `null`) for the
    // current (tenantId, userId). Lets the hint stay hidden on the first paint
    // instead of flashing "unavailable" before the request completes.
    const hasLoaded = ref(false);

    /**
     * Percentage in [0, 100+], rounded for display. Returns 0 when we have
     * never resolved spending or when the user has no row yet (no consumption
     * in this period). A zero-dollar limit is treated as fully exhausted.
     */
    const usedPercentage = computed<number>(() => {
        if (!spending.value) {
            return 0;
        }
        if (spending.value.limit_usd <= 0) {
            return LOCKED_THRESHOLD;
        }
        const raw = (spending.value.used_usd / spending.value.limit_usd) * 100;
        return Math.round(raw);
    });

    const state = computed<ModelSpendingState>(() => {
        if (!hasLoaded.value) {
            return 'unavailable';
        }
        if (spending.value && spending.value.limit_usd <= 0) {
            return 'locked';
        }
        const pct = usedPercentage.value;
        if (pct >= LOCKED_THRESHOLD) return 'locked';
        if (pct >= CRITICAL_THRESHOLD) return 'critical';
        if (pct >= NOTICE_THRESHOLD) return 'notice';
        return 'calm';
    });

    const periodEnd = computed<Date | null>(() => {
        if (!spending.value) return null;
        const d = new Date(spending.value.period_end);
        return Number.isNaN(d.getTime()) ? null : d;
    });

    const daysUntilReset = computed<number | null>(() => {
        if (!periodEnd.value) return null;
        const ms = periodEnd.value.getTime() - Date.now();
        return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
    });

    const resetDateLabel = computed<string | null>(() => {
        if (!periodEnd.value) return null;
        return periodEnd.value.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    });

    function clear() {
        spending.value = null;
        loading.value = false;
        lastTenantId.value = undefined;
        lastUserId.value = undefined;
        hasLoaded.value = false;
    }

    async function load() {
        const tenantId = tenantStore.tenantId;
        const userId = userStore.userId || 'unknown-user';

        if (!authStore.isAuthenticated || !tenantId) {
            clear();
            return;
        }

        const tenantOrUserChanged =
            lastTenantId.value !== tenantId || lastUserId.value !== userId;
        if (tenantOrUserChanged) {
            spending.value = null;
            hasLoaded.value = false;
        }

        const requestId = activeRequestId.value + 1;
        activeRequestId.value = requestId;
        loading.value = true;
        try {
            const result = await getUserModelSpending(tenantId);
            const latestTenantId = tenantStore.tenantId;
            const latestUserId = userStore.userId || 'unknown-user';
            const requestStillRelevant =
                requestId === activeRequestId.value &&
                authStore.isAuthenticated &&
                latestTenantId === tenantId &&
                latestUserId === userId;

            if (!requestStillRelevant) {
                return;
            }

            spending.value = result;
            lastTenantId.value = tenantId;
            lastUserId.value = userId;
            hasLoaded.value = true;
        } catch (error) {
            const latestTenantId = tenantStore.tenantId;
            const latestUserId = userStore.userId || 'unknown-user';
            const requestStillRelevant =
                requestId === activeRequestId.value &&
                authStore.isAuthenticated &&
                latestTenantId === tenantId &&
                latestUserId === userId;

            if (!requestStillRelevant) {
                return;
            }

            // 503 (BillingPeriodUnavailable) and transport errors degrade to
            // "unavailable" only when we have no previous successful value;
            // otherwise we keep showing the last good number until the next load.
            console.warn('Failed to load model spending', error);
            lastTenantId.value = tenantId;
            lastUserId.value = userId;
        } finally {
            if (requestId === activeRequestId.value) {
                loading.value = false;
            }
        }
    }

    async function ensureLoaded() {
        await load();
    }

    async function refresh() {
        await load();
    }

    watch(
        () => authStore.isAuthenticated,
        (isAuthenticated) => {
            if (!isAuthenticated) {
                clear();
            }
        }
    );

    return {
        spending,
        loading,
        hasLoaded,
        usedPercentage,
        state,
        daysUntilReset,
        resetDateLabel,
        ensureLoaded,
        refresh,
        clear
    };
});
